import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import type { StreamingSegment } from '../../hooks/useQuizSession'
import { submitFeedback } from '../../lib/api'
import type { AnswerResult } from '../../types/models'
import EvidenceCard from '../EvidenceCard'
import { Eyebrow } from '../ui'

const CONFIDENCE_OPTIONS = [
  { value: 'FORGOT', label: 'Forgot', desc: 'Complete blank'     },
  { value: 'HARD',   label: 'Hard',   desc: 'Got it with effort' },
  { value: 'GOOD',   label: 'Good',   desc: 'Recalled correctly' },
  { value: 'EASY',   label: 'Easy',   desc: 'Instant recall'     },
] as const

// Strip the "Turn N · X/100" prefix the LLM embeds; the eyebrow renders it instead.
function parseFeedback(feedback: string) {
  return feedback.replace(/^Turn \d+ · \d+\/100\s*/, '').trim()
}

// overflowWrap: 'anywhere' stops a long unbroken LLM token (URL) from widening the page.
const mdComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1.5, overflowWrap: 'anywhere', '&:last-child': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>,
  em: ({ children }) => <Box component="em">{children}</Box>,
  ul: ({ children }) => <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>{children}</Box>,
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ lineHeight: 1.7, mb: 0.5, overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  ),
}

function MarkdownText({ text }: { text: string }) {
  return <ReactMarkdown components={mdComponents}>{text}</ReactMarkdown>
}

// Styled like AiMessage so the turn does not restyle when it lands. `isFirst` drops the top margin.
export function StreamingSegmentBlock({ segment, isFirst }: { segment: StreamingSegment; isFirst: boolean }) {
  if (segment.kind === 'followup') {
    return (
      <Box sx={{ mt: isFirst ? 0 : 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>Follow-up:</Typography>
        <MarkdownText text={segment.text} />
      </Box>
    )
  }
  if (segment.kind === 'closing') {
    return (
      <Box sx={{ mt: isFirst ? 0 : 2, color: 'text.secondary' }}>
        <MarkdownText text={segment.text} />
      </Box>
    )
  }
  return <MarkdownText text={parseFeedback(segment.text)} />
}

export function TypingDots() {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 1 }}>
      {[0, 1, 2].map(i => (
        <Box key={i} sx={{
          width: 7, height: 7, borderRadius: '50%', bgcolor: 'text.disabled',
          animation: 'typingDot 1.4s ease-in-out infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes typingDot': { '0%,60%,100%': { opacity: 0.2 }, '30%': { opacity: 1 } },
        }} />
      ))}
    </Box>
  )
}

function FeedbackControl({ traceId, turn }: { traceId: string; turn: number }) {
  const [rated, setRated] = useState<'UP' | 'DOWN' | null>(null)
  const [failed, setFailed] = useState(false)

  async function rate(rating: 'UP' | 'DOWN') {
    if (rated) return
    setRated(rating)
    setFailed(false)
    try {
      await submitFeedback({ traceId, rating, turn })
    } catch {
      setRated(null)
      setFailed(true)
    }
  }

  if (rated) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Thanks for the feedback!
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Was this helpful?
      </Typography>
      <IconButton size="small" aria-label="Helpful" onClick={() => rate('UP')}>
        <ThumbUpOffAltIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="Not helpful" onClick={() => rate('DOWN')}>
        <ThumbDownOffAltIcon fontSize="small" />
      </IconButton>
      {failed && (
        <Typography variant="caption" color="error.main">Couldn&apos;t send — try again</Typography>
      )}
    </Box>
  )
}

// A blank-feedback turn (the "I don't know" path) still carries a hint worth rendering.
export function hasTutorContent(r: AnswerResult) {
  return Boolean(parseFeedback(r.feedback) || r.followUpQuestion || r.closingMessage)
}

export function AiMessage({ result, isFirstSession }: { result: AnswerResult; isFirstSession: boolean }) {
  const body = parseFeedback(result.feedback)
  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Eyebrow moss>
          {isFirstSession ? '✦ Recall' : `✦ Recall · Turn ${result.conversationStep} · ${result.score}/100`}
        </Eyebrow>
      </Box>
      {body && <MarkdownText text={body} />}
      {result.followUpQuestion && !result.conversationEnded && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>Follow-up:</Typography>
          <MarkdownText text={result.followUpQuestion} />
        </Box>
      )}
      {result.conversationEnded && result.closingMessage && (
        <Box sx={{ mt: 2, color: 'text.secondary' }}>
          <MarkdownText text={result.closingMessage} />
        </Box>
      )}
      {result.conversationEnded && (
        <EvidenceCard
          sourceQuote={result.sourceQuote}
          sourceHighlight={result.sourceHighlight}
        />
      )}
      {result.traceId && (
        <FeedbackControl traceId={result.traceId} turn={result.conversationStep} />
      )}
    </Box>
  )
}

export function UserBubble({ text }: { text: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box sx={{
        bgcolor: 'var(--moss-soft)',
        border: '1px solid var(--moss-line)',
        color: 'var(--ink)',
        p: '12px 18px',
        borderRadius: '18px 18px 4px 18px',
        maxWidth: '80%',
        // Without min-width: 0 an unbroken URL overflows the row past maxWidth.
        minWidth: 0,
      }}>
        <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{text}</Typography>
      </Box>
    </Box>
  )
}

export function ConfidenceSection({
  selectedLabel,
  onSelect,
}: {
  selectedLabel: string | null
  onSelect?: (value: string, label: string) => void
}) {
  const locked = selectedLabel !== null
  return (
    <Box sx={{ my: 2.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
        How confident were you?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {CONFIDENCE_OPTIONS.map(opt => (
          <Button
            key={opt.value}
            variant={selectedLabel === opt.label ? 'contained' : 'outlined'}
            color={opt.value === 'FORGOT' ? 'error' : 'primary'}
            onClick={() => !locked && onSelect?.(opt.value, opt.label)}
            sx={{
              flex: 1,
              flexDirection: 'column',
              py: 1,
              textTransform: 'none',
              minWidth: 0,
              opacity: locked && selectedLabel !== opt.label ? 0.35 : 1,
              pointerEvents: locked ? 'none' : 'auto',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.75 }}>
              {opt.desc}
            </Typography>
          </Button>
        ))}
      </Box>
    </Box>
  )
}
